import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import swal from 'sweetalert'

import { CatchError } from 'Exceptions/exceptions'
import { EMPTY_FORM_IPWHITELISTING } from 'Main/Constant'
import IpWhitelistService from 'Main/service/IpWhitelistingService'
import CommonListApiPaginated from 'views/components/Table/CommonListApiPaginated'
import { getRefreshToken } from 'variables/common'

function IpWhitelistList () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const ipWhitelistService = useMemo(() => new IpWhitelistService(), [])
  const [reloadKey, setReloadKey] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [formData, setFormData] = useState(EMPTY_FORM_IPWHITELISTING)

  const getWhitelists = useCallback(() => ipWhitelistService.getData(), [ipWhitelistService])

  const handleListError = useCallback((error) => {
    CatchError(error, navigate, refreshToken)
  }, [navigate, refreshToken])

  const handleAdd = () => {
    setFormData(EMPTY_FORM_IPWHITELISTING)
    setModalOpen(true)
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setFormData((current) => ({ ...current, [name]: value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    try {
      setSubmitting(true)
      await ipWhitelistService.create(formData)
      setModalOpen(false)
      await swal('Success!', 'IP Whitelist Added', 'success')
      setReloadKey((current) => current + 1)
    } catch (error) {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) swal('Error', 'Failed to add IP whitelist. Please verify the input data.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = useCallback((id) => {
    swal('Delete IP Whitelist', 'Are You Sure Delete This IP Whitelist?', 'warning', {
      buttons: { cancel: 'Cancel', delete: { text: 'Delete', value: 'delete' } },
      dangerMode: true
    }).then(async (value) => {
      if (value !== 'delete') return

      try {
        await ipWhitelistService.deleteById(id)
        await swal('Success!', 'IP Whitelist Deleted', 'success')
        setReloadKey((current) => current + 1)
      } catch (error) {
        const caught = CatchError(error, navigate, refreshToken)
        if (caught !== undefined) swal('Error', 'Failed to delete IP whitelist', 'error')
      }
    })
  }, [ipWhitelistService, navigate, refreshToken])

  const columns = useMemo(() => [
    { header: 'Nama Aplikasi', accessorKey: 'device_name', maxWidth: 650 },
    { header: 'Dibuat Pada', accessorKey: 'date', maxWidth: 250 },
    { header: 'IP Address', accessorKey: 'ip', maxWidth: 250 },
    {
      header: 'Action',
      id: 'actions',
      maxWidth: 80,
      enableSorting: false,
      filterable: false,
      exportable: false,
      cell: ({ row }) => (
        <button type="button" onClick={() => handleDelete(row.original.id)} className="btn btn-danger" title="Delete IP Whitelist">
          <i className="fa-solid fa-trash-can" />
        </button>
      )
    }
  ], [handleDelete])

  const menu = (
    <div className="text-center">
      <button type="button" onClick={handleAdd} className="btn btn-success" title="Tambah Whitelist">
        <i className="fa-solid fa-pen-to-square" />&nbsp;&nbsp; Tambah Whitelist
      </button>
    </div>
  )

  return (
    <div className="page-content">
      <section>
        <CommonListApiPaginated
          header="List IP Whitelisting"
          subtitle=""
          columns={columns}
          getData={getWhitelists}
          menu={menu}
          reloadKey={reloadKey}
          loadingLabel="Loading IP whitelist data..."
          emptyLabel="No IP Whitelist entries found"
          onError={handleListError}
        />
      </section>

      {modalOpen && (
        <>
          <div className="modal-backdrop fade show" onClick={submitting ? undefined : () => setModalOpen(false)} />
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">Add IP Whitelist</h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setModalOpen(false)} disabled={submitting} />
                  </div>

                  <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <div className="mb-3">
                      <label htmlFor="deviceName" className="form-label">Host Name</label>
                      <input id="deviceName" name="deviceName" type="text" className="form-control" placeholder="Insert Host Name" value={formData.deviceName} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="ip" className="form-label">IP Address</label>
                      <input id="ip" name="ip" type="text" className="form-control" placeholder="Insert IP Address" value={formData.ip} onChange={handleInputChange} required />
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-light text-black" onClick={() => setModalOpen(false)} disabled={submitting}>Cancel</button>
                    <button type="submit" className="btn btn-success" disabled={submitting}>
                      <i className={`fa-solid ${submitting ? 'fa-circle-notch fa-spin' : 'fa-floppy-disk'}`} />{' '}
                      {submitting ? 'Saving...' : 'Add Whitelist'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default IpWhitelistList
